// Firebase for the account features, loaded on first use: pages that never call getFirebase() (the tool page for a
// visitor who isn't ad-free) never download it. Only types are imported statically.
import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { FUNCTIONS_REGION, type CallableName } from '../../functions/src/contract.ts';

export interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

export class AccountNotConfiguredError extends Error {
  constructor() {
    super('The account features are not configured (VITE_FIREBASE_* is missing).');
    this.name = 'AccountNotConfiguredError';
  }
}

/** True when the Firebase web config is set. Without it the account features stay hidden. */
export function isAccountConfigured(): boolean {
  const env = import.meta.env;
  return Boolean(
    env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_AUTH_DOMAIN && env.VITE_FIREBASE_PROJECT_ID && env.VITE_FIREBASE_APP_ID,
  );
}

/** VITE_FIREBASE_EMULATORS=1: Auth, Firestore and Functions go to the local emulators (ports from firebase.json). */
export function usesEmulators(): boolean {
  return import.meta.env.VITE_FIREBASE_EMULATORS === '1';
}

let services: Promise<FirebaseServices> | null = null;

/** The Firebase app and services, initialised once. Rejects with AccountNotConfiguredError without a config. */
export function getFirebase(): Promise<FirebaseServices> {
  if (!isAccountConfigured()) return Promise.reject(new AccountNotConfiguredError());
  if (!services) {
    const loading = initialize();
    services = loading;
    // A failed download (offline) may be retried by the next call.
    loading.catch(() => {
      if (services === loading) services = null;
    });
  }
  return services;
}

async function initialize(): Promise<FirebaseServices> {
  const env = import.meta.env;
  const siteKey = env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;
  const emulators = usesEmulators();

  // Download everything first, so that a failure leaves nothing half-initialised.
  const [appModule, authModule, firestoreModule, functionsModule, appCheckModule] = await Promise.all([
    import('firebase/app'),
    import('firebase/auth'),
    import('firebase/firestore'),
    import('firebase/functions'),
    siteKey || emulators ? import('firebase/app-check') : Promise.resolve(null),
  ]);

  const app =
    appModule.getApps()[0] ??
    appModule.initializeApp({
      apiKey: env.VITE_FIREBASE_API_KEY,
      authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: env.VITE_FIREBASE_PROJECT_ID,
      appId: env.VITE_FIREBASE_APP_ID,
    });

  // App Check before any request: the callables refuse requests without a token.
  if (emulators && siteKey) {
    // Use a debug token instead of reCAPTCHA. The browser console prints it on first use: register it in the Firebase
    // console (App Check → Apps → Manage debug tokens) of the project in VITE_FIREBASE_PROJECT_ID.
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  if (appCheckModule && siteKey) {
    appCheckModule.initializeAppCheck(app, {
      provider: new appCheckModule.ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else if (appCheckModule && emulators) {
    // Emulators without a reCAPTCHA key (the end-to-end tests): the Functions emulator decodes App Check tokens
    // without verifying them, so an unsigned stand-in lets the callables through. Only a build made with
    // VITE_FIREBASE_EMULATORS=1 runs this, and Firebase's real App Check would refuse the token.
    const part = (value: object) => btoa(JSON.stringify(value)).replace(/=+$/, '');
    const token = `${part({ alg: 'none', typ: 'JWT' })}.${part({ sub: 'emulator', iss: 'emulator', exp: 4102444800 })}.`;
    appCheckModule.initializeAppCheck(app, {
      provider: new appCheckModule.CustomProvider({
        getToken: async () => ({ token, expireTimeMillis: Date.now() + 3_600_000 }),
      }),
      isTokenAutoRefreshEnabled: false,
    });
  }

  const auth = authModule.getAuth(app);
  if (typeof document !== 'undefined' && document.documentElement.lang) {
    auth.languageCode = document.documentElement.lang; // the Google sign-in window in the page's language
  }
  const db = firestoreModule.getFirestore(app);
  const functions = functionsModule.getFunctions(app, FUNCTIONS_REGION);

  if (emulators) {
    authModule.connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    firestoreModule.connectFirestoreEmulator(db, '127.0.0.1', 8080);
    functionsModule.connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }

  return { app, auth, db, functions };
}

/** Calls a callable function (App Check and the signed-in user's ID token go with it). */
export async function callFunction<Request, Response>(name: CallableName, data: Request): Promise<Response> {
  const [{ functions }, { httpsCallable }] = await Promise.all([getFirebase(), import('firebase/functions')]);
  const result = await httpsCallable<Request, Response>(functions, name)(data);
  return result.data;
}
